import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { CHANNEL_SHORT_NAME_MAP, IChannel, PROTOCOL } from '@/entities/channel';
import { Dialog, TDialogBase } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { Link } from '@/shared/ui/lib/Link';
import { Stack, Typography } from '@/shared/ui';
import { ControlledTextField } from '@/shared/ui/TextField';

import { useOzonConnect } from '../../model/ozon/useOzonConnect';
import { ozonConnectFormDataSchema } from '../../model/schemas';
import { ChannelIconWithLabel } from '../ChannelIconWithLabel';

const FORM_ID = 'ozon-connect-form';
const OZON_KEYS_URL = 'https://docs.ozon.com/global/api/intro/';

interface OzonConnectFormProps extends TDialogBase {
  channel: IChannel;
  onAuthorize?: () => void;
}

export const OzonConnectForm = ({
  open,
  onClose,
  onAuthorize,
  channel,
}: OzonConnectFormProps) => {
  const { t } = useTranslation();
  const { connect, isPending } = useOzonConnect();

  const {
    control,
    handleSubmit,
    reset,
  } = useForm({
    resolver: zodResolver(ozonConnectFormDataSchema(t)),
    defaultValues: {
      clientId: '',
      apiKey: '',
      name: channel.name ?? '',
    },
    mode: 'onChange',
  });

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const onSubmit = handleSubmit(async (data) => {
    await connect({
      channelId: channel.id,
      clientId: data.clientId,
      apiKey: data.apiKey,
      name: data.name,
    });
    reset();
    onAuthorize?.();
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('channels:ozon.authTitle')}
      additionalTitleSlot={
        <ChannelIconWithLabel
          label={CHANNEL_SHORT_NAME_MAP[PROTOCOL.OZON]}
          protocol={PROTOCOL.OZON}
        />
      }
      leftButtonSlot={
        <Button variant="outlined" onClick={handleClose}>
          {t('channels:buttons.closeModal')}
        </Button>
      }
      rightButtonSlot={
        <Button
          variant="contained"
          type="submit"
          form={FORM_ID}
          loading={isPending}
        >
          {t('channels:ozon.connect')}
        </Button>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit}>
        <Stack spacing={2}>
          <ControlledTextField
            name="clientId"
            control={control}
            autoFocus
            label={t('channels:ozon.clientId')}
          />
          <ControlledTextField
            name="apiKey"
            control={control}
            type="password"
            label={t('channels:ozon.apiKey')}
          />
          <Typography variant="body2">
            <Link href={OZON_KEYS_URL} target="_blank" rel="noopener noreferrer">
              {t('channels:ozon.keysLink')}
            </Link>
          </Typography>
          <ControlledTextField
            name="name"
            control={control}
            label={t('channels:ozon.name')}
          />
        </Stack>
      </form>
    </Dialog>
  );
};
